import { Dispatch, SetStateAction, useMemo } from 'react'
import { useConsumerCart, useQueryParams } from 'shop/hooks'
import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import CenterModal from './CenterModal'
import { Button } from '../Controls'
import ProductInfoItem from './ProductInfoItem'
import { itemScollListShadows, thinScrollbar } from '../common'
import { defaultTheme } from 'shop/theme'
import {
  trackGA4CustomDeeplinkClickBackToOldCart,
  trackGA4CustomDeeplinkClickCreateNewCart
} from 'tracker/GA/custom'
import { useNavigate, useParams } from 'react-router-dom'
import { createReadableDateString } from 'shop/utils'
import { format } from 'date-fns'

export type CartSwitchModalProps = {
  setShowCartSwitchModal: Dispatch<SetStateAction<boolean>>
  switchCartStore: () => void
  storeName: string
  cartFulfillmentDatetime?: string
}

type CartSwitchGlossary = {
  switchCartStore: () => void
  oldStoreName: string
  newStoreName: string
  cartFulfillmentDatetime?: string
}

type CartSwitchGlossaryResponse = {
  title: string
  message: string[]
  primaryButton: {
    message: string
    onClick?: () => void
  }
}

const cartSwitchGlossary = ({
  switchCartStore,
  oldStoreName,
  newStoreName,
  cartFulfillmentDatetime
}: CartSwitchGlossary): CartSwitchGlossaryResponse => {
  const oldCartDatetime = cartFulfillmentDatetime
    ? new Date(cartFulfillmentDatetime)
    : null
  const oldCartDatetimeString = oldCartDatetime
    ? ` (${createReadableDateString(oldCartDatetime, 'long')} ${format(oldCartDatetime, 'HH:mm')})`
    : ''
  return {
    title: `Create a new cart?`,
    message: [
      `You already have an active cart on our ${oldStoreName} store${oldCartDatetimeString}.`,
      `If you continue shopping at ${newStoreName}, the following items will be removed from your previous cart:`
    ],
    primaryButton: {
      message: 'Create new cart',
      onClick: () => {
        trackGA4CustomDeeplinkClickCreateNewCart('slerpGA4Tracker')
        trackGA4CustomDeeplinkClickCreateNewCart('merchantGA4Tracker')
        switchCartStore()
      }
    }
  }
}

const CartSwitchModal = ({
  setShowCartSwitchModal,
  switchCartStore,
  storeName,
  cartFulfillmentDatetime
}: CartSwitchModalProps) => {
  const { cart } = useConsumerCart()
  const { deleteQueryParam } = useQueryParams()
  const params = useParams<{ slug: string }>()
  const navigate = useNavigate()

  const urlSlug = params.slug

  const orderItems = useMemo(() => cart?.orderItems, [cart?.orderItems])
  const oldStoreName = useMemo(() => cart?.store.name || '', [cart?.store.name])

  const handleGoBackClick = () => {
    trackGA4CustomDeeplinkClickBackToOldCart('slerpGA4Tracker')
    trackGA4CustomDeeplinkClickBackToOldCart('merchantGA4Tracker')
    setShowCartSwitchModal(false)
    if (!cart?.store.slug) {
      navigate(-1)
      return
    }

    const isSameStore = cart.store.slug === urlSlug

    // Remove query param if it exists before navigating
    deleteQueryParam(['fulfillment_type', 'date', 'time'])
    // If user is on same store as the one which they have a cart with and wants to go back, ensure we remove fType query param & reload page
    if (isSameStore) {
      navigate(`/store/${cart.store.slug}`)
      window.location.reload()
      return
    }

    navigate(`/store/${cart.store.slug}`)
  }

  if (!orderItems?.length) {
    return <></>
  }

  const cartSwitchContent = cartSwitchGlossary({
    switchCartStore,
    oldStoreName,
    newStoreName: storeName,
    cartFulfillmentDatetime
  })

  return (
    <CenterModal
      handleCloseModal={() => setShowCartSwitchModal(false)}
      zIndex={(defaultTheme.zIndex?.['productModal'] || 200) + 1}
    >
      <Container>
        <Header>{cartSwitchContent.title}</Header>
        <Content>
          {cartSwitchContent.message.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </Content>
        <ItemsContainer isScrollable={orderItems.length > 2}>
          {orderItems?.map((item) => (
            <ProductInfoItem item={item} key={item.id} />
          ))}
        </ItemsContainer>
        <ButtonContainer>
          <Button
            fontWeight={'bold'}
            onClick={cartSwitchContent.primaryButton.onClick}
          >
            {cartSwitchContent.primaryButton.message}
          </Button>
          <Button
            variant='secondary'
            fontWeight={'bold'}
            onClick={handleGoBackClick}
          >
            Return to previous cart
          </Button>
        </ButtonContainer>
      </Container>
    </CenterModal>
  )
}

const Container = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    padding: '32px'
  })
)

const Header = styled.h1<StyledHTMLElement>(({ theme }) => ({
  fontSize: '20px',
  marginBottom: '24px'
}))

const Content = styled.div<StyledHTMLElement>(({ theme }) => ({
  color: theme.colors['textMute'],
  marginBottom: '40px',
  '> p': {
    margin: '12px 0'
  }
}))

const ItemsContainer = styled.ul<StyledHTMLElement & { isScrollable: boolean }>(
  ({ theme, isScrollable }) => ({
    display: 'flex',
    flexDirection: 'column',
    margin: '40px 0',
    maxHeight: '175px', // ~70px per item - 2.5 items
    ...(isScrollable
      ? {
          overflowY: 'auto',
          marginRight: '-10px',
          paddingRight: '10px',
          ...itemScollListShadows(),

          [theme['mediaQueries']['viewport7']]: {
            marginRight: '-15px',
            paddingRight: '5px'
          },
          ...thinScrollbar(theme)
        }
      : {})
  })
)

const ButtonContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '15px'
}))

export default CartSwitchModal
